import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './GemQuest.css';
import { database } from '../../firebase'; // Import Firebase
import { ref, set, get } from 'firebase/database';

const GEM_TYPES = ['💎', '🔶', '🔷', '🟡', '🟢', '🔴'];

// Module-level debounce tracking
let lastFirebaseSave = {};
let lastProfileSave = {};

// Firebase leaderboard functions
const saveScoreToFirebase = async (playerName, score, level, userId) => {
  try {
    console.log('Attempting Firebase save:', { playerName, score, level, userId });
    
    // Debounce mechanism - prevent rapid saves for the same user
    const now = Date.now();
    if (lastFirebaseSave[userId] && (now - lastFirebaseSave[userId]) < 2000) {
      console.log('Debounced Firebase save - too soon after last save');
      return;
    }
    lastFirebaseSave[userId] = now;
    
    // Use userId as the key to ensure one record per user
    const userRef = ref(database, `gemquest_leaderboard/${userId}`);
    
    // Check if user already exists
    const snapshot = await get(userRef);
    let shouldUpdate = true;
    
    if (snapshot.exists()) {
      const existingData = snapshot.val();
      console.log('Existing Firebase data:', existingData);
      // Only update if new score is higher, or same score but higher level
      if (score < existingData.score || (score === existingData.score && level <= existingData.level)) {
        shouldUpdate = false;
        console.log('Score not updated - current score is better or equal');
        return;
      }
    }
    
    if (shouldUpdate) {
      const playerData = {
        playerName,
        score,
        level,
        userId,
        timestamp: Date.now(),
        date: new Date().toISOString()
      };
      
      // Set (upsert) the user record
      await set(userRef, playerData);
      console.log(snapshot.exists() ? 'Score updated in Firebase successfully' : 'New score saved to Firebase successfully');
      console.log('Saved data:', playerData);
    }
  } catch (error) {
    console.error('Error saving score to Firebase:', error);
  }
};

const loadLeaderboardFromFirebase = async () => {
  try {
    const scoresRef = ref(database, 'gemquest_leaderboard');
    const snapshot = await get(scoresRef);
    
    if (snapshot.exists()) {
      const scoresData = snapshot.val();
      const leaderboardArray = Object.entries(scoresData)
        .map(([userId, value]) => ({
          userId: userId,
          ...value
        }))
        .filter(entry => 
          entry.score !== undefined && 
          entry.score !== null && 
          entry.playerName && 
          entry.level !== undefined
        ); // Filter out invalid entries
      
      // Sort by score descending, then by level descending
      const sortedLeaderboard = leaderboardArray
        .sort((a, b) => {
          const scoreA = Number(a.score) || 0;
          const scoreB = Number(b.score) || 0;
          const levelA = Number(a.level) || 1;
          const levelB = Number(b.level) || 1;
          
          if (scoreB !== scoreA) return scoreB - scoreA;
          return levelB - levelA;
        })
        .slice(0, 10); // Top 10
      
      return sortedLeaderboard;
    }
    return [];
  } catch (error) {
    console.error('Error loading leaderboard from Firebase:', error);
    return getLeaderboard(); // Fallback to localStorage
  }
};

// User management utilities
const getUserProfile = () => {
  const profile = localStorage.getItem('gemquest_user_profile');
  return profile ? JSON.parse(profile) : null;
};

const saveUserProfile = (name, score = 0, level = 1, dailyData = null) => {
  const profile = {
    name,
    score,
    level,
    lastPlayed: new Date().toISOString(),
    dailyData: dailyData || getDailyPlayData()
  };
  localStorage.setItem('gemquest_user_profile', JSON.stringify(profile));
  localStorage.setItem('gemquest_user_name', name);
  return profile;
};

const getUserId = () => {
  let userId = localStorage.getItem('gemquest_user_id');
  if (!userId) {
    userId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem('gemquest_user_id', userId);
    console.log('Generated new userId:', userId);
  } else {
    console.log('Using existing userId:', userId);
  }
  return userId;
};

// Daily play management utilities
const getDailyPlayData = () => {
  const data = localStorage.getItem('gemquest_daily_data');
  return data ? JSON.parse(data) : {
    lastPlayDate: null,
    gamesPlayedToday: 0,
    lifelines: 0,
    totalLifelinesEarned: 0,
    dailyHighScore: 0,
    canPlayUntil: null
  };
};

const saveDailyPlayData = (data) => {
  localStorage.setItem('gemquest_daily_data', JSON.stringify(data));
};

const isNewDay = (lastDate) => {
  if (!lastDate) return true;
  const last = new Date(lastDate);
  const now = new Date();
  return last.toDateString() !== now.toDateString();
};

const canPlayToday = () => {
  const dailyData = getDailyPlayData();
  const now = new Date();
  
  // Check if it's a new day
  if (isNewDay(dailyData.lastPlayDate)) {
    return { canPlay: true, isNewDay: true };
  }
  
  // Check if user is in cooldown period
  if (dailyData.canPlayUntil && now < new Date(dailyData.canPlayUntil)) {
    const timeLeft = new Date(dailyData.canPlayUntil) - now;
    return { 
      canPlay: false, 
      isNewDay: false, 
      timeLeft: Math.ceil(timeLeft / (1000 * 60 * 60)) // hours left
    };
  }
  
  return { canPlay: true, isNewDay: false };
};

const earnLifeline = (score) => {
  const dailyData = getDailyPlayData();
  const milestones = [
    { score: 5000, lifelines: 1 },
    { score: 15000, lifelines: 2 },
    { score: 30000, lifelines: 3 },
    { score: 50000, lifelines: 4 },
    { score: 75000, lifelines: 5 },
    { score: 100000, lifelines: 6 }
  ];
  
  for (const milestone of milestones) {
    if (score >= milestone.score && dailyData.dailyHighScore < milestone.score) {
      dailyData.lifelines += milestone.lifelines;
      dailyData.totalLifelinesEarned += milestone.lifelines;
      dailyData.dailyHighScore = Math.max(dailyData.dailyHighScore, score);
      saveDailyPlayData(dailyData);
      return { score: milestone.score, lifelines: milestone.lifelines };
    }
  }
  
  dailyData.dailyHighScore = Math.max(dailyData.dailyHighScore, score);
  saveDailyPlayData(dailyData);
  return null;
};

const consumeLifeline = () => {
  const dailyData = getDailyPlayData();
  if (dailyData.lifelines > 0) {
    dailyData.lifelines -= 1;
    saveDailyPlayData(dailyData);
    return true;
  }
  return false;
};

const endDailyPlay = () => {
  const dailyData = getDailyPlayData();
  const now = new Date();
  const cooldownEnd = new Date(now.getTime() + (12 * 60 * 60 * 1000)); // 12 hours from now
  
  dailyData.canPlayUntil = cooldownEnd.toISOString();
  dailyData.gamesPlayedToday += 1;
  dailyData.lastPlayDate = now.toISOString();
  saveDailyPlayData(dailyData);
  
  return cooldownEnd;
};

const resetDailyData = () => {
  const dailyData = getDailyPlayData();
  const now = new Date();
  
  dailyData.lastPlayDate = now.toISOString();
  dailyData.gamesPlayedToday = 0;
  dailyData.dailyHighScore = 0;
  dailyData.canPlayUntil = null;
  // Keep lifelines and totalLifelinesEarned across days
  
  saveDailyPlayData(dailyData);
  return dailyData;
};

// Leaderboard utilities
const getLeaderboard = () => {
  // Run migration first to ensure compatibility
  migrateLeaderboardFormat();
  
  const leaderboard = localStorage.getItem('gemquest_leaderboard');
  return leaderboard ? JSON.parse(leaderboard) : [];
};

const updateLeaderboard = async (score, level, userId, userName) => {
  console.log('Updating leaderboard:', { score, level, userId, userName });
  
  // Save to Firebase first
  await saveScoreToFirebase(userName, score, level, userId);
  
  // Then update localStorage for offline access
  const leaderboard = getLeaderboard();
  const userIndex = leaderboard.findIndex(entry => entry.userId === userId);
  
  const newEntry = {
    userId,
    playerName: userName, // Match Firebase structure
    score: score, // Match Firebase structure
    level: level, // Match Firebase structure
    lastPlayed: new Date().toISOString(),
    totalGames: 1
  };
  
  if (userIndex >= 0) {
    // Update existing user
    const existingEntry = leaderboard[userIndex];
    newEntry.score = Math.max(existingEntry.score || 0, score);
    newEntry.level = Math.max(existingEntry.level || 1, level);
    newEntry.totalGames = (existingEntry.totalGames || 0) + 1;
    leaderboard[userIndex] = newEntry;
    console.log('Updated existing leaderboard entry:', newEntry);
  } else {
    // Add new user
    leaderboard.push(newEntry);
    console.log('Added new leaderboard entry:', newEntry);
  }
  
  // Sort by score and keep top 10
  leaderboard.sort((a, b) => {
    const scoreA = Number(a.score) || 0;
    const scoreB = Number(b.score) || 0;
    const levelA = Number(a.level) || 1;
    const levelB = Number(b.level) || 1;
    
    if (scoreB !== scoreA) return scoreB - scoreA;
    return levelB - levelA;
  });
  const topLeaderboard = leaderboard.slice(0, 10);
  
  localStorage.setItem('gemquest_leaderboard', JSON.stringify(topLeaderboard));
  console.log('Leaderboard updated:', topLeaderboard);
  return topLeaderboard;
};

// Migration function to convert old leaderboard format to new format
const migrateLeaderboardFormat = () => {
  try {
    const leaderboard = localStorage.getItem('gemquest_leaderboard');
    if (leaderboard) {
      const entries = JSON.parse(leaderboard);
      const migratedEntries = entries.map(entry => {
        // Check if it's old format (has userName/highScore) and convert
        if (entry.userName && !entry.playerName) {
          return {
            ...entry,
            playerName: entry.userName,
            score: entry.highScore || entry.score || 0,
            level: entry.highestLevel || entry.level || 1
          };
        }
        // Ensure required fields exist
        return {
          ...entry,
          playerName: entry.playerName || entry.userName || 'Unknown Player',
          score: entry.score || entry.highScore || 0,
          level: entry.level || entry.highestLevel || 1
        };
      });
      localStorage.setItem('gemquest_leaderboard', JSON.stringify(migratedEntries));
    }
  } catch (error) {
    console.error('Error migrating leaderboard format:', error);
    // Clear corrupted data
    localStorage.removeItem('gemquest_leaderboard');
  }
};

const GemQuest = () => {
  const BOARD_SIZE = 8;
  
  // Initialize from saved profile
  const savedProfile = getUserProfile();
  
  // User authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [userProfile, setUserProfile] = useState(savedProfile);
  
  // Game states - initialized from saved profile
  const [board, setBoard] = useState([]);
  const [score, setScore] = useState(savedProfile?.score || 0);
  const [selectedGem, setSelectedGem] = useState(null);
  const [moves, setMoves] = useState(30);
  const [gameOver, setGameOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [level, setLevel] = useState(savedProfile?.level || 1);
  const [target, setTarget] = useState(savedProfile ? Math.floor(1000 * Math.pow(1.5, savedProfile.level - 1)) : 1000);
  const [combo, setCombo] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [celebrations, setCelebrations] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [fallingGems, setFallingGems] = useState(new Set());
  const [matchedGems, setMatchedGems] = useState(new Set());
  const [levelComplete, setLevelComplete] = useState(false);
  
  // New states for leaderboard and user management
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [userName, setUserName] = useState(savedProfile?.name || '');
  const [leaderboard, setLeaderboard] = useState(getLeaderboard());
  const [userId] = useState(() => {
    const id = getUserId();
    console.log('Component initialized with userId:', id);
    return id;
  });
  const [sessionScore, setSessionScore] = useState(savedProfile?.score || 0);
  const [sessionLevel, setSessionLevel] = useState(savedProfile?.level || 1);

  // Daily limit and lifeline states
  const [dailyData, setDailyData] = useState(getDailyPlayData());
  const [showDailyLimitModal, setShowDailyLimitModal] = useState(false);
  const [showLifelineModal, setShowLifelineModal] = useState(false);
  const [showLifelineEarnedModal, setShowLifelineEarnedModal] = useState(false);
  const [earnedLifelineScore, setEarnedLifelineScore] = useState(null);
  const [noMovesWithLifelines, setNoMovesWithLifelines] = useState(false);
  const [countdownTime, setCountdownTime] = useState('');

  // Memoize GEM_TYPES to prevent unnecessary re-renders
  const gemTypes = useMemo(() => GEM_TYPES, []);

  // Audio context management for mobile compatibility
  const [audioContext, setAudioContext] = useState(null);
  
  // Initialize audio context with user interaction (required for mobile)
  const initializeAudioContext = useCallback(() => {
    if (!audioContext) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        
        // Resume context if suspended (mobile requirement)
        if (ctx.state === 'suspended') {
          ctx.resume().then(() => {
            console.log('Audio context resumed');
          });
        }
        
        setAudioContext(ctx);
        return ctx;
      } catch (error) {
        console.log('Audio not supported');
        return null;
      }
    }
    return audioContext;
  }, [audioContext]);

  // Enhanced sound generation functions with mobile fixes
  const playMatchSound = useCallback((matchLength, comboLevel) => {
    if (!soundEnabled) return;
    
    const ctx = initializeAudioContext();
    if (!ctx) return;
    
    try {
      // Resume context if needed (mobile fix)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // More attractive bell-like sound with magical sparkles
      const baseNote = 523.25; // C5 - Higher pitch for better mobile speakers
      const magicalChords = {
        3: [baseNote, baseNote * 1.26, baseNote * 1.5, baseNote * 2], // C Major with octave
        4: [baseNote * 1.5, baseNote * 1.89, baseNote * 2.25, baseNote * 3], // G Major (brighter)
        5: [baseNote * 0.89, baseNote * 1.12, baseNote * 1.33, baseNote * 1.78, baseNote * 2.37], // Magical pentatonic
        6: [baseNote * 2, baseNote * 2.52, baseNote * 3, baseNote * 3.56, baseNote * 4, baseNote * 4.76] // Celestial cascade
      };
      
      const frequencies = magicalChords[Math.min(matchLength, 6)] || magicalChords[3];
      const duration = 0.4 + (matchLength * 0.08); // Longer, more satisfying duration
      
      // Create main chord with enhanced mobile-friendly settings
      frequencies.forEach((freq, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        const filterNode = ctx.createBiquadFilter();
        const compressor = ctx.createDynamicsCompressor();
        
        // Enhanced audio chain for mobile clarity
        oscillator.connect(filterNode);
        filterNode.connect(compressor);
        compressor.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        // Mobile-optimized compressor settings
        compressor.threshold.setValueAtTime(-12, ctx.currentTime);
        compressor.knee.setValueAtTime(6, ctx.currentTime);
        compressor.ratio.setValueAtTime(8, ctx.currentTime);
        compressor.attack.setValueAtTime(0.003, ctx.currentTime);
        compressor.release.setValueAtTime(0.1, ctx.currentTime);
        
        // Warm, crystalline filter
        filterNode.type = 'lowpass';
        filterNode.frequency.setValueAtTime(3500 + (comboLevel * 800), ctx.currentTime);
        filterNode.Q.setValueAtTime(3.5, ctx.currentTime);
        
        // Slight detuning for richness
        const detune = (Math.random() - 0.5) * 8;
        oscillator.frequency.setValueAtTime(freq + detune, ctx.currentTime);
        
        // Enhanced envelope with mobile-optimized volume
        const startTime = ctx.currentTime + index * 0.015;
        const peakGain = 0.25 / frequencies.length; // Higher base volume for mobile
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(peakGain, startTime + 0.03);
        gainNode.gain.setValueAtTime(peakGain * 0.8, startTime + 0.08);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        
        // Warmer oscillator type
        oscillator.type = 'triangle';
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      });
      
      // Enhanced sparkle effect for longer matches
      if (matchLength > 3) {
        setTimeout(() => {
          for (let i = 0; i < matchLength - 2; i++) {
            const sparkleOsc = ctx.createOscillator();
            const sparkleGain = ctx.createGain();
            const sparkleFilter = ctx.createBiquadFilter();
            
            sparkleOsc.connect(sparkleFilter);
            sparkleFilter.connect(sparkleGain);
            sparkleGain.connect(ctx.destination);
            
            // Bright, magical sparkle frequencies
            const sparkleFreq = 2093 + (i * 349); // High notes in pentatonic
            sparkleOsc.frequency.setValueAtTime(sparkleFreq, ctx.currentTime);
            sparkleOsc.frequency.exponentialRampToValueAtTime(sparkleFreq * 1.5, ctx.currentTime + 0.12);
            
            // Crystalline filter
            sparkleFilter.type = 'bandpass';
            sparkleFilter.frequency.setValueAtTime(sparkleFreq, ctx.currentTime);
            sparkleFilter.Q.setValueAtTime(8, ctx.currentTime);
            
            const sparkleStart = ctx.currentTime + (i * 0.06);
            sparkleGain.gain.setValueAtTime(0, sparkleStart);
            sparkleGain.gain.linearRampToValueAtTime(0.15, sparkleStart + 0.02);
            sparkleGain.gain.exponentialRampToValueAtTime(0.001, sparkleStart + 0.18);
            
            sparkleOsc.type = 'sine';
            sparkleOsc.start(sparkleStart);
            sparkleOsc.stop(sparkleStart + 0.18);
          }
        }, 120);
      }
      
      // Add magical chime for exceptional matches
      if (matchLength > 5) {
        setTimeout(() => {
          const chimeOsc = ctx.createOscillator();
          const chimeGain = ctx.createGain();
          const chimeDelay = ctx.createDelay();
          const chimeFeedback = ctx.createGain();
          
          chimeOsc.connect(chimeGain);
          chimeGain.connect(chimeDelay);
          chimeDelay.connect(chimeFeedback);
          chimeFeedback.connect(chimeDelay);
          chimeFeedback.connect(ctx.destination);
          chimeGain.connect(ctx.destination);
          
          chimeDelay.delayTime.setValueAtTime(0.08, ctx.currentTime);
          chimeFeedback.gain.setValueAtTime(0.4, ctx.currentTime);
          
          chimeOsc.frequency.setValueAtTime(3136, ctx.currentTime); // Very high C
          chimeGain.gain.setValueAtTime(0, ctx.currentTime);
          chimeGain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.02);
          chimeGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
          
          chimeOsc.type = 'sine';
          chimeOsc.start(ctx.currentTime);
          chimeOsc.stop(ctx.currentTime + 0.4);
        }, 200);
      }
    } catch (error) {
      console.log('Audio playback failed:', error);
    }
  }, [soundEnabled, initializeAudioContext]);

  const playComboSound = useCallback((comboLevel) => {
    if (!soundEnabled || comboLevel < 2) return;
    
    const ctx = initializeAudioContext();
    if (!ctx) return;
    
    try {
      // Resume context if needed
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Epic ascending magical scale for combo - enhanced pentatonic
      const magicalScale = [523.25, 587.33, 659.25, 783.99, 880, 1046.5, 1174.66, 1318.51, 1567.98]; // C5 pentatonic extended
      const comboNotes = magicalScale.slice(0, Math.min(comboLevel + 2, 9));
      
      comboNotes.forEach((freq, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        const delayNode = ctx.createDelay();
        const feedbackGain = ctx.createGain();
        const reverbGain = ctx.createGain();
        const compressor = ctx.createDynamicsCompressor();
        
        // Enhanced magical reverb/delay chain
        oscillator.connect(gainNode);
        gainNode.connect(delayNode);
        delayNode.connect(feedbackGain);
        feedbackGain.connect(delayNode);
        
        // Add compressor for mobile consistency
        gainNode.connect(compressor);
        delayNode.connect(reverbGain);
        reverbGain.connect(compressor);
        compressor.connect(ctx.destination);
        
        // Mobile-optimized compressor
        compressor.threshold.setValueAtTime(-15, ctx.currentTime);
        compressor.knee.setValueAtTime(8, ctx.currentTime);
        compressor.ratio.setValueAtTime(6, ctx.currentTime);
        
        delayNode.delayTime.setValueAtTime(0.12, ctx.currentTime);
        feedbackGain.gain.setValueAtTime(0.35, ctx.currentTime);
        reverbGain.gain.setValueAtTime(0.4, ctx.currentTime);
        
        const startTime = ctx.currentTime + index * 0.09;
        oscillator.frequency.setValueAtTime(freq, startTime);
        
        // Enhanced magical shimmer effect
        oscillator.frequency.linearRampToValueAtTime(freq * 1.03, startTime + 0.06);
        oscillator.frequency.linearRampToValueAtTime(freq * 0.98, startTime + 0.12);
        oscillator.frequency.linearRampToValueAtTime(freq, startTime + 0.18);
        
        // Mobile-optimized volume levels
        const noteGain = 0.18 + (comboLevel * 0.02);
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(noteGain, startTime + 0.03);
        gainNode.gain.setValueAtTime(noteGain * 0.7, startTime + 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.35);
        
        oscillator.type = 'triangle';
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.35);
      });
      
      // Enhanced ethereal pad sound for higher combos
      if (comboLevel > 3) {
        const padOsc1 = ctx.createOscillator();
        const padOsc2 = ctx.createOscillator();
        const padGain = ctx.createGain();
        const padFilter = ctx.createBiquadFilter();
        const padLFO = ctx.createOscillator();
        const lfoGain = ctx.createGain();
        
        // Create rich pad with LFO modulation
        padOsc1.connect(padFilter);
        padOsc2.connect(padFilter);
        padFilter.connect(padGain);
        padGain.connect(ctx.destination);
        
        // LFO for magical tremolo effect
        padLFO.connect(lfoGain);
        lfoGain.connect(padGain.gain);
        
        padFilter.type = 'lowpass';
        padFilter.frequency.setValueAtTime(1200, ctx.currentTime);
        padFilter.Q.setValueAtTime(6, ctx.currentTime);
        
        // Rich harmonic content
        padOsc1.frequency.setValueAtTime(130.81, ctx.currentTime); // Low C
        padOsc2.frequency.setValueAtTime(196.22, ctx.currentTime); // G below
        
        // LFO for magical shimmer
        padLFO.frequency.setValueAtTime(4.5, ctx.currentTime);
        lfoGain.gain.setValueAtTime(0.03, ctx.currentTime);
        
        const padStart = ctx.currentTime + 0.05;
        const padDuration = 1.2 + (comboLevel * 0.15);
        const padVolume = 0.12 + (comboLevel * 0.02);
        
        padGain.gain.setValueAtTime(0, padStart);
        padGain.gain.linearRampToValueAtTime(padVolume, padStart + 0.15);
        padGain.gain.setValueAtTime(padVolume * 0.8, padStart + 0.4);
        padGain.gain.exponentialRampToValueAtTime(0.001, padStart + padDuration);
        
        padOsc1.type = 'sawtooth';
        padOsc2.type = 'triangle';
        padLFO.type = 'sine';
        
        padOsc1.start(padStart);
        padOsc2.start(padStart);
        padLFO.start(padStart);
        padOsc1.stop(padStart + padDuration);
        padOsc2.stop(padStart + padDuration);
        padLFO.stop(padStart + padDuration);
      }
      
      // Ultra combo celestial bells for very high combos
      if (comboLevel > 5) {
        setTimeout(() => {
          const celestialFreqs = [2093, 2349, 2637, 3136, 3520]; // Very high notes
          celestialFreqs.forEach((freq, i) => {
            const bellOsc = ctx.createOscillator();
            const bellGain = ctx.createGain();
            const bellFilter = ctx.createBiquadFilter();
            
            bellOsc.connect(bellFilter);
            bellFilter.connect(bellGain);
            bellGain.connect(ctx.destination);
            
            bellFilter.type = 'bandpass';
            bellFilter.frequency.setValueAtTime(freq, ctx.currentTime);
            bellFilter.Q.setValueAtTime(12, ctx.currentTime);
            
            const bellTime = ctx.currentTime + i * 0.08;
            bellOsc.frequency.setValueAtTime(freq, bellTime);
            bellGain.gain.setValueAtTime(0, bellTime);
            bellGain.gain.linearRampToValueAtTime(0.15, bellTime + 0.02);
            bellGain.gain.exponentialRampToValueAtTime(0.001, bellTime + 0.6);
            
            bellOsc.type = 'sine';
            bellOsc.start(bellTime);
            bellOsc.stop(bellTime + 0.6);
          });
        }, 300);
      }
    } catch (error) {
      console.log('Combo audio playback failed:', error);
    }
  }, [soundEnabled, initializeAudioContext]);

  // Attractive sliding/swapping sound with mobile optimization
  const playSwapSound = useCallback(() => {
    if (!soundEnabled) return;
    
    const ctx = initializeAudioContext();
    if (!ctx) return;
    
    try {
      // Resume context if needed
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Create an enhanced "magical swoosh" sound with multiple layers
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const osc3 = ctx.createOscillator(); // Additional layer for richness
      const gain1 = ctx.createGain();
      const gain2 = ctx.createGain();
      const gain3 = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      const compressor = ctx.createDynamicsCompressor();
      
      // Enhanced audio chain
      osc1.connect(gain1);
      osc2.connect(gain2);
      osc3.connect(gain3);
      gain1.connect(filter);
      gain2.connect(filter);
      gain3.connect(filter);
      filter.connect(compressor);
      compressor.connect(ctx.destination);
      
      // Mobile-optimized compressor
      compressor.threshold.setValueAtTime(-18, ctx.currentTime);
      compressor.knee.setValueAtTime(4, ctx.currentTime);
      compressor.ratio.setValueAtTime(4, ctx.currentTime);
      
      // Enhanced filter for magical swoosh
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(600, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
      filter.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.2);
      filter.Q.setValueAtTime(4, ctx.currentTime);
      
      // Layer 1: Main descending sweep
      osc1.frequency.setValueAtTime(800, ctx.currentTime);
      osc1.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.18);
      osc1.type = 'triangle';
      
      // Layer 2: Ascending sweep (slightly delayed for richness)
      osc2.frequency.setValueAtTime(300, ctx.currentTime + 0.04);
      osc2.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.22);
      osc2.type = 'sine';
      
      // Layer 3: Mid-frequency warble for magic
      osc3.frequency.setValueAtTime(500, ctx.currentTime + 0.02);
      osc3.frequency.linearRampToValueAtTime(600, ctx.currentTime + 0.1);
      osc3.frequency.linearRampToValueAtTime(450, ctx.currentTime + 0.2);
      osc3.type = 'sawtooth';
      
      // Enhanced envelopes with mobile-optimized volume
      const baseVolume = 0.2; // Higher for mobile
      
      // Layer 1 envelope
      gain1.gain.setValueAtTime(0, ctx.currentTime);
      gain1.gain.linearRampToValueAtTime(baseVolume, ctx.currentTime + 0.04);
      gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      
      // Layer 2 envelope
      gain2.gain.setValueAtTime(0, ctx.currentTime + 0.04);
      gain2.gain.linearRampToValueAtTime(baseVolume * 0.7, ctx.currentTime + 0.08);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
      
      // Layer 3 envelope
      gain3.gain.setValueAtTime(0, ctx.currentTime + 0.02);
      gain3.gain.linearRampToValueAtTime(baseVolume * 0.4, ctx.currentTime + 0.06);
      gain3.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.18);
      osc2.start(ctx.currentTime + 0.04);
      osc2.stop(ctx.currentTime + 0.22);
      osc3.start(ctx.currentTime + 0.02);
      osc3.stop(ctx.currentTime + 0.2);
      
    } catch (error) {
      console.log('Swap audio playback failed:', error);
    }
  }, [soundEnabled, initializeAudioContext]);

  // Special reward sound for earning lifelines
  const playLifelineEarnedSound = useCallback(() => {
    if (!soundEnabled) return;
    
    const ctx = initializeAudioContext();
    if (!ctx) return;
    
    try {
      // Resume context if needed
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Magical lifeline earned fanfare
      const rewardProgression = [
        [523.25, 659.25, 783.99], // C Major
        [587.33, 739.99, 880], // D Major
        [659.25, 830.61, 987.77], // E Major
        [783.99, 987.77, 1174.66, 1567.98] // G Major with high note
      ];
      
      rewardProgression.forEach((chord, chordIndex) => {
        chord.forEach((freq, noteIndex) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          const delay = ctx.createDelay();
          const delayGain = ctx.createGain();
          
          // Magical delay chain
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(delay);
          delay.connect(delayGain);
          delayGain.connect(ctx.destination);
          gain.connect(ctx.destination);
          
          delay.delayTime.setValueAtTime(0.15, ctx.currentTime);
          delayGain.gain.setValueAtTime(0.4, ctx.currentTime);
          
          // Bright, celebratory filter
          filter.type = 'lowpass';
          filter.frequency.setValueAtTime(3500, ctx.currentTime);
          filter.Q.setValueAtTime(2, ctx.currentTime);
          
          const startTime = ctx.currentTime + chordIndex * 0.2;
          osc.frequency.setValueAtTime(freq, startTime);
          
          // Reward envelope
          const noteVolume = 0.15 / chord.length;
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(noteVolume, startTime + 0.05);
          gain.gain.setValueAtTime(noteVolume * 0.8, startTime + 0.15);
          gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.5);
          
          osc.type = 'triangle';
          osc.start(startTime);
          osc.stop(startTime + 0.5);
        });
      });
      
      // Sparkle cascade for the reward
      setTimeout(() => {
        const sparkles = [1046.5, 1318.51, 1567.98, 2093, 2637.02];
        sparkles.forEach((freq, index) => {
          const sparkle = ctx.createOscillator();
          const sparkleGain = ctx.createGain();
          const sparkleFilter = ctx.createBiquadFilter();
          
          sparkle.connect(sparkleFilter);
          sparkleFilter.connect(sparkleGain);
          sparkleGain.connect(ctx.destination);
          
          sparkleFilter.type = 'bandpass';
          sparkleFilter.frequency.setValueAtTime(freq, ctx.currentTime);
          sparkleFilter.Q.setValueAtTime(20, ctx.currentTime);
          
          const sparkleTime = ctx.currentTime + index * 0.1;
          sparkle.frequency.setValueAtTime(freq, sparkleTime);
          
          sparkleGain.gain.setValueAtTime(0, sparkleTime);
          sparkleGain.gain.linearRampToValueAtTime(0.2, sparkleTime + 0.02);
          sparkleGain.gain.exponentialRampToValueAtTime(0.001, sparkleTime + 0.6);
          
          sparkle.type = 'sine';
          sparkle.start(sparkleTime);
          sparkle.stop(sparkleTime + 0.6);
        });
      }, 500);
      
    } catch (error) {
      console.log('Lifeline reward audio failed:', error);
    }
  }, [soundEnabled, initializeAudioContext]);

  // Enhanced victory/level complete sound with epic fanfare
  const playVictorySound = useCallback(() => {
    if (!soundEnabled) return;
    
    const ctx = initializeAudioContext();
    if (!ctx) return;
    
    try {
      // Resume context if needed
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Epic triumphant fanfare with rich harmonies and progression
      const victoryProgression = [
        [523.25, 659.25, 783.99, 1046.5], // C Major chord (C5)
        [587.33, 739.99, 880, 1174.66], // D Major chord  
        [659.25, 830.61, 987.77, 1318.51], // E Major chord
        [783.99, 987.77, 1174.66, 1567.98], // G Major chord
        [1046.5, 1318.51, 1567.98, 2093] // Final C Major octave
      ];
      
      // Main fanfare progression
      victoryProgression.forEach((chord, chordIndex) => {
        chord.forEach((freq, noteIndex) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          const compressor = ctx.createDynamicsCompressor();
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(compressor);
          compressor.connect(ctx.destination);
          
          // Mobile-optimized compressor
          compressor.threshold.setValueAtTime(-12, ctx.currentTime);
          compressor.knee.setValueAtTime(8, ctx.currentTime);
          compressor.ratio.setValueAtTime(6, ctx.currentTime);
          
          // Bright, celebratory filter
          filter.type = 'lowpass';
          filter.frequency.setValueAtTime(4000, ctx.currentTime);
          filter.Q.setValueAtTime(1.5, ctx.currentTime);
          
          const startTime = ctx.currentTime + chordIndex * 0.4;
          osc.frequency.setValueAtTime(freq, startTime);
          
          // Grand, sustained envelope with mobile-optimized volume
          const chordVolume = 0.2 / chord.length; // Higher base volume
          gain.gain.setValueAtTime(0, startTime);
          gain.gain.linearRampToValueAtTime(chordVolume, startTime + 0.08);
          gain.gain.setValueAtTime(chordVolume * 0.8, startTime + 0.3);
          gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.8);
          
          // Rich timbres: bass with sawtooth, others with triangle
          osc.type = noteIndex === 0 ? 'sawtooth' : 'triangle';
          osc.start(startTime);
          osc.stop(startTime + 0.8);
        });
      });
      
      // Enhanced celestial sparkles cascade
      setTimeout(() => {
        const sparkleSequences = [
          [1046.5, 1318.5, 1568, 2093, 2637], // First cascade
          [1174.66, 1479.98, 1760, 2349.32, 2959.96], // Second cascade
          [1318.51, 1661.22, 1975.53, 2637.02, 3322.44] // Final cascade
        ];
        
        sparkleSequences.forEach((sequence, seqIndex) => {
          sequence.forEach((freq, index) => {
            const sparkleOsc = ctx.createOscillator();
            const sparkleGain = ctx.createGain();
            const sparkleFilter = ctx.createBiquadFilter();
            const sparkleDelay = ctx.createDelay();
            const delayGain = ctx.createGain();
            
            // Enhanced sparkle chain with delay
            sparkleOsc.connect(sparkleFilter);
            sparkleFilter.connect(sparkleGain);
            sparkleGain.connect(sparkleDelay);
            sparkleDelay.connect(delayGain);
            delayGain.connect(ctx.destination);
            sparkleGain.connect(ctx.destination);
            
            // Crystalline filter
            sparkleFilter.type = 'bandpass';
            sparkleFilter.frequency.setValueAtTime(freq, ctx.currentTime);
            sparkleFilter.Q.setValueAtTime(15, ctx.currentTime);
            
            // Magical delay
            sparkleDelay.delayTime.setValueAtTime(0.1, ctx.currentTime);
            delayGain.gain.setValueAtTime(0.3, ctx.currentTime);
            
            const sparkleTime = ctx.currentTime + (seqIndex * 0.2) + (index * 0.08);
            sparkleOsc.frequency.setValueAtTime(freq, sparkleTime);
            
            // Bright, magical envelope
            sparkleGain.gain.setValueAtTime(0, sparkleTime);
            sparkleGain.gain.linearRampToValueAtTime(0.18, sparkleTime + 0.03);
            sparkleGain.gain.exponentialRampToValueAtTime(0.001, sparkleTime + 0.4);
            
            sparkleOsc.type = 'sine';
            sparkleOsc.start(sparkleTime);
            sparkleOsc.stop(sparkleTime + 0.4);
          });
        });
      }, 600);
      
      // Grand finale timpani-like boom
      setTimeout(() => {
        const boom = ctx.createOscillator();
        const boomGain = ctx.createGain();
        const boomFilter = ctx.createBiquadFilter();
        
        boom.connect(boomFilter);
        boomFilter.connect(boomGain);
        boomGain.connect(ctx.destination);
        
        boomFilter.type = 'lowpass';
        boomFilter.frequency.setValueAtTime(150, ctx.currentTime);
        boomFilter.Q.setValueAtTime(8, ctx.currentTime);
        
        boom.frequency.setValueAtTime(65.41, ctx.currentTime); // Low C
        boom.frequency.exponentialRampToValueAtTime(32.7, ctx.currentTime + 0.1);
        
        boomGain.gain.setValueAtTime(0, ctx.currentTime);
        boomGain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.02);
        boomGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
        
        boom.type = 'sawtooth';
        boom.start(ctx.currentTime);
        boom.stop(ctx.currentTime + 0.6);
      }, 1800);
      
    } catch (error) {
      console.log('Victory audio playback failed:', error);
    }
  }, [soundEnabled, initializeAudioContext]);

  // Add celebration effects
  const addCelebration = useCallback((matchPositions, matchLength, comboLevel) => {
    const newCelebrations = matchPositions.map((pos, index) => ({
      id: Date.now() + index,
      x: (pos.col + 0.5) * (100 / 8) + '%', // Use percentage-based positioning
      y: (pos.row + 0.5) * (100 / 8) + '%',
      type: matchLength > 4 ? 'mega' : comboLevel > 1 ? 'combo' : 'normal',
      matchLength,
      comboLevel
    }));
    
    setCelebrations(prev => [...prev, ...newCelebrations]);
    
    // Remove celebrations after animation
    setTimeout(() => {
      setCelebrations(prev => prev.filter(cel => !newCelebrations.find(newCel => newCel.id === cel.id)));
    }, 1200);
  }, []);

  // Initialize board with random gems
  const initializeBoard = useCallback(() => {
    const newBoard = [];
    for (let row = 0; row < BOARD_SIZE; row++) {
      const newRow = [];
      for (let col = 0; col < BOARD_SIZE; col++) {
        let gem;
        do {
          gem = gemTypes[Math.floor(Math.random() * gemTypes.length)];
        } while (
          (col >= 2 && newRow[col - 1] === gem && newRow[col - 2] === gem) ||
          (row >= 2 && newBoard[row - 1] && newBoard[row - 1][col] === gem && 
           newBoard[row - 2] && newBoard[row - 2][col] === gem)
        );
        newRow.push(gem);
      }
      newBoard.push(newRow);
    }
    return newBoard;
  }, [gemTypes]);

  // Check for matches of 3 or more
  const findMatches = useCallback((currentBoard) => {
    const matches = [];
    
    // Check horizontal matches
    for (let row = 0; row < BOARD_SIZE; row++) {
      let count = 1;
      let currentGem = currentBoard[row][0];
      
      for (let col = 1; col < BOARD_SIZE; col++) {
        if (currentBoard[row][col] === currentGem && currentGem !== null) {
          count++;
        } else {
          if (count >= 3) {
            for (let i = col - count; i < col; i++) {
              matches.push({ row, col: i });
            }
          }
          count = 1;
          currentGem = currentBoard[row][col];
        }
      }
      if (count >= 3) {
        for (let i = BOARD_SIZE - count; i < BOARD_SIZE; i++) {
          matches.push({ row, col: i });
        }
      }
    }

    // Check vertical matches
    for (let col = 0; col < BOARD_SIZE; col++) {
      let count = 1;
      let currentGem = currentBoard[0][col];
      
      for (let row = 1; row < BOARD_SIZE; row++) {
        if (currentBoard[row][col] === currentGem && currentGem !== null) {
          count++;
        } else {
          if (count >= 3) {
            for (let i = row - count; i < row; i++) {
              matches.push({ row: i, col });
            }
          }
          count = 1;
          currentGem = currentBoard[row][col];
        }
      }
      if (count >= 3) {
        for (let i = BOARD_SIZE - count; i < BOARD_SIZE; i++) {
          matches.push({ row: i, col });
        }
      }
    }

    return matches;
  }, []);

  // Remove matches and calculate score
  const removeMatches = useCallback((currentBoard, matches) => {
    const newBoard = currentBoard.map(row => [...row]);
    let points = 0;
    
    matches.forEach(match => {
      newBoard[match.row][match.col] = null;
      points += 10;
    });

    // Bonus points for longer matches
    if (matches.length > 3) {
      points += (matches.length - 3) * 20;
    }

    // Combo bonus
    const comboBonus = combo * 5;
    points += comboBonus;

    return { newBoard, points };
  }, [combo]);

  // Drop gems down to fill empty spaces
  const dropGems = useCallback((currentBoard) => {
    const newBoard = currentBoard.map(row => [...row]);
    
    for (let col = 0; col < BOARD_SIZE; col++) {
      let writeIndex = BOARD_SIZE - 1;
      
      for (let row = BOARD_SIZE - 1; row >= 0; row--) {
        if (newBoard[row][col] !== null) {
          newBoard[writeIndex][col] = newBoard[row][col];
          if (writeIndex !== row) {
            newBoard[row][col] = null;
          }
          writeIndex--;
        }
      }
      
      // Fill empty spaces with new random gems
      for (let row = writeIndex; row >= 0; row--) {
        newBoard[row][col] = gemTypes[Math.floor(Math.random() * gemTypes.length)];
      }
    }
    
    return newBoard;
  }, [gemTypes]);

  // Process all matches and cascading effects
  const processMatches = useCallback(async (currentBoard) => {
    let newBoard = currentBoard;
    let totalPoints = 0;
    let currentCombo = 0;
    let hasMatches = true;

    while (hasMatches) {
      const matches = findMatches(newBoard);
      
      if (matches.length > 0) {
        currentCombo++;
        setCombo(currentCombo);
        
        // Track matched gems for visual effects
        const matchedSet = new Set(matches.map(match => `${match.row}-${match.col}`));
        setMatchedGems(matchedSet);
        
        // Play sounds and add celebrations
        playMatchSound(matches.length, currentCombo);
        if (currentCombo > 1) {
          playComboSound(currentCombo);
        }
        addCelebration(matches, matches.length, currentCombo);
        
        // Add visual highlight for matched gems with longer delay
        const { newBoard: boardAfterRemoval, points } = removeMatches(newBoard, matches);
        totalPoints += points;
        
        // Update board to show removal and add visual delay
        setBoard(boardAfterRemoval);
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Clear matched gems and track falling gems
        setMatchedGems(new Set());
        
        // Identify which gems will fall
        const fallingSet = new Set();
        for (let col = 0; col < 8; col++) {
          let hasEmpty = false;
          for (let row = 7; row >= 0; row--) {
            if (boardAfterRemoval[row][col] === null) {
              hasEmpty = true;
            } else if (hasEmpty) {
              fallingSet.add(`${row}-${col}`);
            }
          }
        }
        setFallingGems(fallingSet);
        
        // Drop gems down with visible transition
        newBoard = dropGems(boardAfterRemoval);
        setBoard(newBoard);
        
        // Add longer delay for gems settling to be visible
        await new Promise(resolve => setTimeout(resolve, 250));
        
        // Clear falling gems
        setFallingGems(new Set());
      } else {
        hasMatches = false;
      }
    }

    setScore(prev => {
      const newScore = prev + totalPoints;
      
      // Check for lifeline earning
      const earnedMilestone = earnLifeline(newScore);
      if (earnedMilestone) {
        setEarnedLifelineScore(earnedMilestone);
        setShowLifelineEarnedModal(true);
        setDailyData(getDailyPlayData()); // Refresh daily data
        
        // Play special lifeline earned sound
        setTimeout(() => {
          playLifelineEarnedSound();
        }, 300);
        
        // Stronger haptic feedback for lifeline rewards
        if (navigator.vibrate) {
          navigator.vibrate([100, 100, 100, 100, 200]); // Celebration pattern
        }
      }
      
      return newScore;
    });
    
    // Reset combo after a delay
    setTimeout(() => setCombo(0), 500);
    
    return newBoard;
  }, [findMatches, removeMatches, dropGems, playMatchSound, playComboSound, addCelebration]);

  // Check if move is valid
  const isValidMove = useCallback((from, to) => {
    const rowDiff = Math.abs(from.row - to.row);
    const colDiff = Math.abs(from.col - to.col);
    
    return (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
  }, []);

  // Swap two gems
  const swapGems = useCallback((board, gem1, gem2) => {
    const newBoard = board.map(row => [...row]);
    const temp = newBoard[gem1.row][gem1.col];
    newBoard[gem1.row][gem1.col] = newBoard[gem2.row][gem2.col];
    newBoard[gem2.row][gem2.col] = temp;
    return newBoard;
  }, []);

  // Handle gem click with audio context initialization and haptic feedback
  const handleGemClick = async (row, col) => {
    if (isProcessing || gameOver) return;

    // Initialize audio context on first user interaction (mobile requirement)
    initializeAudioContext();

    // Subtle haptic feedback for mobile (if available)
    if (navigator.vibrate) {
      navigator.vibrate(50); // 50ms vibration
    }

    const clickedGem = { row, col };

    if (!selectedGem) {
      setSelectedGem(clickedGem);
    } else {
      if (selectedGem.row === row && selectedGem.col === col) {
        setSelectedGem(null);
        return;
      }

      if (isValidMove(selectedGem, clickedGem)) {
        setIsProcessing(true);
        
        // Play satisfying swap sound
        playSwapSound();
        
        // Stronger haptic feedback for successful swaps
        if (navigator.vibrate) {
          navigator.vibrate([80, 50, 80]); // Pattern: vibrate-pause-vibrate
        }
        
        const swappedBoard = swapGems(board, selectedGem, clickedGem);
        const matches = findMatches(swappedBoard);

        if (matches.length > 0) {
          // First update the board to show the swap
          setBoard(swappedBoard);
          setMoves(prev => prev - 1);
          
          // Add a small delay to show the swap before processing matches
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Process matches and get final board
          const finalBoard = await processMatches(swappedBoard);
          setBoard(finalBoard);
        } else {
          // Invalid move - show swap briefly then revert
          setBoard(swappedBoard);
          await new Promise(resolve => setTimeout(resolve, 200));
          setBoard(board); // Revert to original board
        }

        setSelectedGem(null);
        setIsProcessing(false);
      } else {
        setSelectedGem(clickedGem);
      }
    }
  };

  // Check for possible moves
  const hasPossibleMoves = useCallback((currentBoard) => {
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        // Check right
        if (col < BOARD_SIZE - 1) {
          const testBoard = swapGems(currentBoard, { row, col }, { row, col: col + 1 });
          if (findMatches(testBoard).length > 0) return true;
        }
        // Check down
        if (row < BOARD_SIZE - 1) {
          const testBoard = swapGems(currentBoard, { row, col }, { row: row + 1, col });
          if (findMatches(testBoard).length > 0) return true;
        }
      }
    }
    return false;
  }, [swapGems, findMatches]);

  // Shuffle board if no moves available
  const shuffleBoard = useCallback(() => {
    let newBoard;
    do {
      newBoard = initializeBoard();
    } while (!hasPossibleMoves(newBoard));
    setBoard(newBoard);
  }, [initializeBoard, hasPossibleMoves]);

  // Initialize user authentication
  useEffect(() => {
    initializeUserSession();
    
    // Auto-authenticate if user profile exists and was loaded
    if (savedProfile && savedProfile.name) {
      setIsAuthenticated(true);
    }
  }, []);

  // Load Firebase leaderboard on component mount
  useEffect(() => {
    const loadLeaderboard = async () => {
      try {
        const firebaseLeaderboard = await loadLeaderboardFromFirebase();
        if (firebaseLeaderboard.length > 0) {
          setLeaderboard(firebaseLeaderboard);
        }
      } catch (error) {
        console.error('Error loading Firebase leaderboard:', error);
      }
    };
    
    loadLeaderboard();
  }, []);

  // Initialize game - Check daily play status (only after authentication)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const playStatus = canPlayToday();
    
    if (!playStatus.canPlay) {
      setShowDailyLimitModal(true);
      return;
    }
    
    if (playStatus.isNewDay) {
      const newDailyData = resetDailyData();
      setDailyData(newDailyData);
    }
    
    const initialBoard = initializeBoard();
    setBoard(initialBoard);
  }, [isAuthenticated, initializeBoard]);

  // Check game over conditions - Auto-advance or use lifelines
  // Auto-progression when moves run out
  useEffect(() => {
    if (moves <= 0 && !gameOver && !levelComplete) {
      if (score >= target) {
        // Level complete - add celebration and auto-advance
        addCelebration([{row: 4, col: 4}], 8, 5); // Center celebration for level up
        playVictorySound(); // Play enhanced victory sound
        setLevelComplete(true);
        
        // Auto-advance to next level after a short delay
        // Use a callback to ensure we get the latest score
        setTimeout(() => {
          proceedToNextLevel();
        }, 2000);
      } else {
        // Check if lifelines are available
        if (dailyData.lifelines > 0) {
          setNoMovesWithLifelines(true);
          setShowLifelineModal(true);
        } else {
          // No lifelines - end game for the day
          setGameOver(true);
        }
      }
    }
  }, [moves, score, target, shuffleBoard, gameOver, levelComplete, addCelebration, playVictorySound, level, userId, userName, dailyData.lifelines]);

  // Check for no possible moves - Enhanced with lifeline system
  useEffect(() => {
    if (board.length > 0 && !hasPossibleMoves(board) && !isProcessing) {
      if (dailyData.lifelines > 0) {
        // Player has lifelines available
        setNoMovesWithLifelines(true);
        setShowLifelineModal(true);
      } else {
        // No lifelines - end game and enforce daily cooldown
        setShowHint(true);
        setTimeout(() => {
          const cooldownEnd = endDailyPlay();
          setShowDailyLimitModal(true);
          setShowHint(false);
        }, 1000);
      }
    }
  }, [board, hasPossibleMoves, isProcessing, dailyData.lifelines]);

  // Save progress when score or level changes
  useEffect(() => {
    if (isAuthenticated && userName && (score > 0 || level > 1)) {
      // Debounce the save to avoid too many writes
      const saveTimeout = setTimeout(() => {
        // Only save to Firebase if enough time has passed
        const now = Date.now();
        if (!lastProfileSave.timestamp || now - lastProfileSave.timestamp > 1000) {
          saveUserProfile(userName, score, level, dailyData);
          lastProfileSave = { timestamp: now, score, level };
          console.log('Auto-saved progress:', { score, level, timestamp: now });
        }
      }, 1000);
      
      return () => clearTimeout(saveTimeout);
    }
  }, [score, level, isAuthenticated, userName, dailyData]);

  // Immediate Firebase save when score increases significantly (to capture gameplay progress)
  useEffect(() => {
    if (isAuthenticated && userName && score > 0) {
      // Save immediately to Firebase when score increases
      const timeoutId = setTimeout(() => {
        updateLeaderboard(score, level, userId, userName).then(newLeaderboard => {
          setLeaderboard(newLeaderboard);
          console.log('Immediate Firebase save for score update:', { score, level });
        }).catch(error => {
          console.error('Error saving score immediately:', error);
        });
      }, 500); // Shorter delay for immediate saves
      
      return () => clearTimeout(timeoutId);
    }
  }, [score]); // Only trigger on score changes

  // Auto-save progress when score or level changes
  useEffect(() => {
    if (isAuthenticated && userName && (score > 0 || level > 1)) {
      const timeoutId = setTimeout(() => {
        // Only save to localStorage for auto-save
        // Firebase saving is handled by specific events (level complete, game over, etc.)
        saveUserProfile(userName, score, level, dailyData);
        
        console.log('Auto-saved progress to localStorage:', { score, level });
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [score, level, isAuthenticated, userName, userId, dailyData]);

  // Countdown timer update effect
  useEffect(() => {
    let interval;
    if (showDailyLimitModal) {
      interval = setInterval(() => {
        setCountdownTime(getCountdownDisplay());
      }, 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showDailyLimitModal]);

  // Reset game
  const resetGame = async () => {
    // Update leaderboard before reset with current score
    const finalScore = score;
    const finalLevel = level;
    console.log('Resetting game - Final score/level:', { finalScore, finalLevel });
    
    if (finalScore > 0 || finalLevel > 1) {
      try {
        const newLeaderboard = await updateLeaderboard(finalScore, finalLevel, userId, userName);
        setLeaderboard(newLeaderboard);
        console.log('Updated leaderboard during reset with final score:', finalScore);
      } catch (error) {
        console.error('Error updating leaderboard during reset:', error);
      }
    }
    
    // Save user progress before reset
    saveUserProfile(userName, 0, 1, dailyData);
    
    setScore(0);
    setSessionScore(0);
    setMoves(30);
    setLevel(1);
    setSessionLevel(1);
    setTarget(1000);
    setGameOver(false);
    setLevelComplete(false);
    setSelectedGem(null);
    setCombo(0);
    setShowHint(false);
    setCelebrations([]);
    setFallingGems(new Set());
    setMatchedGems(new Set());
    const newBoard = initializeBoard();
    setBoard(newBoard);
  };

  // Update user name
  const handleNameUpdate = (newName) => {
    if (newName.trim()) {
      const trimmedName = newName.trim();
      setUserName(trimmedName);
      setUserName(trimmedName);
      setShowNamePrompt(false);
    }
  };

  // User authentication functions
  const initializeUserSession = () => {
    const profile = getUserProfile();
    if (profile) {
      // User has a saved profile, show welcome modal
      setUserProfile(profile);
      setShowWelcomeModal(true);
    } else {
      // New user, show new user modal
      setShowNewUserModal(true);
    }
  };

  const handleContinueAsUser = () => {
    if (userProfile) {
      // Initialize audio context on user interaction
      initializeAudioContext();
      
      // Load saved game state
      setUserName(userProfile.name);
      setScore(userProfile.score);
      setLevel(userProfile.level);
      setTarget(Math.floor(1000 * Math.pow(1.5, userProfile.level - 1))); // Consistent target calculation
      setDailyData(userProfile.dailyData || getDailyPlayData());
      
      // Save updated profile with correct level
      saveUserProfile(userProfile.name, userProfile.score, userProfile.level, userProfile.dailyData);
      
      // Also save to Firebase to ensure data is synced
      saveScoreToFirebase(userProfile.name, userProfile.score, userProfile.level, userId).catch(console.error);
      
      setIsAuthenticated(true);
      setShowWelcomeModal(false);
      
      console.log('Loaded user profile:', { name: userProfile.name, score: userProfile.score, level: userProfile.level });
    }
  };

  const handleCreateNewUser = (newName) => {
    if (newName.trim()) {
      // Initialize audio context on user interaction
      initializeAudioContext();
      
      const trimmedName = newName.trim();
      
      // Clear profile data but keep userId for consistency
      localStorage.removeItem('gemquest_user_profile');
      localStorage.removeItem('gemquest_user_name');
      localStorage.removeItem('gemquest_daily_data');
      // Keep userId to maintain Firebase record consistency
      
      console.log('Creating new user profile, keeping userId:', userId);
      
      // Create new profile
      setUserName(trimmedName);
      setScore(0);
      setLevel(1);
      setTarget(1000);
      setDailyData(getDailyPlayData());
      
      // Save new profile
      saveUserProfile(trimmedName, 0, 1);
      
      setIsAuthenticated(true);
      setShowNewUserModal(false);
      setShowWelcomeModal(false);
    }
  };

  const handleChangeUser = () => {
    setShowWelcomeModal(false);
    setShowNewUserModal(true);
  };

  // Use lifeline to continue playing
  const handleUseLifeline = () => {
    if (consumeLifeline()) {
      const updatedDailyData = getDailyPlayData(); // Refresh daily data
      setDailyData(updatedDailyData);
      shuffleBoard();
      setMoves(prev => prev + 10); // Bonus moves for using lifeline
      setShowLifelineModal(false);
      setNoMovesWithLifelines(false);

      // Check if this was the last lifeline
      if (updatedDailyData.lifelines === 0) {
        // Show a warning that this was the last lifeline
        setTimeout(() => {
          // You could add a toast notification here if desired
        }, 1000);
      }
    }
  };

  // Decline lifeline - end game for the day
  const handleDeclineLifeline = () => {
    const cooldownEnd = endDailyPlay();
    setShowLifelineModal(false);
    setShowDailyLimitModal(true);
    setNoMovesWithLifelines(false);
  };

  // Format time remaining for display
  const formatTimeRemaining = (hours) => {
    if (hours < 1) return "less than 1 hour";
    return hours === 1 ? "1 hour" : `${hours} hours`;
  };

  // Real-time countdown function
  const getCountdownDisplay = () => {
    const playStatus = canPlayToday();
    if (playStatus.canPlay) {
      return "Available Now";
    }
    
    const data = getDailyPlayData();
    if (!data.canPlayUntil) {
      return "Available Now";
    }
    
    const now = new Date();
    const endTime = new Date(data.canPlayUntil);
    const timeLeft = endTime - now;
    
    if (timeLeft <= 0) {
      return "Available Now";
    }
    
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Advance to next level - Auto progression
  const proceedToNextLevel = async () => {
    const newLevel = level + 1;
    const newTarget = Math.floor(1000 * Math.pow(1.5, newLevel - 1)); // Consistent target calculation
    
    // Get current score from state
    const currentScore = score;
    console.log('Proceeding to next level with score:', currentScore);
    
    // Automatically advance to next level
    setLevel(newLevel);
    setMoves(30); // Standard moves for next level
    setTarget(newTarget);
    setLevelComplete(false);
    
    // Update leaderboard with current progress (this will handle Firebase saving)
    const newLeaderboard = await updateLeaderboard(currentScore, newLevel, userId, userName);
    setLeaderboard(newLeaderboard);
    
    // Save progress to localStorage with updated level
    setTimeout(() => {
      saveUserProfile(userName, currentScore, newLevel, dailyData);
      console.log('Profile saved with current score:', { userName, score: currentScore, level: newLevel });
    }, 100);
    
    setSelectedGem(null);
    setCombo(0);
    setShowHint(false);
    setCelebrations([]);
  };

  return (
    <div className="gem-quest-container">
      <div className="gem-quest-header">
        <h1>💎 Gem Quest</h1>
        {isAuthenticated && (
          <div className="game-controls">
            <button 
              className="leaderboard-btn"
              onClick={async () => {
                setShowLeaderboard(true);
                try {
                  const firebaseLeaderboard = await loadLeaderboardFromFirebase();
                  if (firebaseLeaderboard.length > 0) {
                    setLeaderboard(firebaseLeaderboard);
                  }
                } catch (error) {
                  console.error('Error refreshing leaderboard:', error);
                }
              }}
              title="View Leaderboard"
            >
              🏆
            </button>
            <button 
              className={`sound-toggle ${soundEnabled ? 'enabled' : 'disabled'}`}
              onClick={() => {
                const newSoundState = !soundEnabled;
                setSoundEnabled(newSoundState);
                
                // Initialize audio context and play welcome sound when enabling
                if (newSoundState) {
                  const ctx = initializeAudioContext();
                  if (ctx) {
                    // Play a pleasant welcome chime
                    setTimeout(() => {
                      try {
                        const welcomeOsc = ctx.createOscillator();
                        const welcomeGain = ctx.createGain();
                        const welcomeFilter = ctx.createBiquadFilter();
                        
                        welcomeOsc.connect(welcomeFilter);
                        welcomeFilter.connect(welcomeGain);
                        welcomeGain.connect(ctx.destination);
                        
                        welcomeFilter.type = 'lowpass';
                        welcomeFilter.frequency.setValueAtTime(2000, ctx.currentTime);
                        welcomeFilter.Q.setValueAtTime(2, ctx.currentTime);
                        
                        welcomeOsc.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
                        welcomeOsc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1); // E5
                        welcomeOsc.frequency.setValueAtTime(783.99, ctx.currentTime + 0.2); // G5
                        
                        welcomeGain.gain.setValueAtTime(0, ctx.currentTime);
                        welcomeGain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.05);
                        welcomeGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
                        
                        welcomeOsc.type = 'triangle';
                        welcomeOsc.start(ctx.currentTime);
                        welcomeOsc.stop(ctx.currentTime + 0.4);
                      } catch (error) {
                        console.log('Welcome sound failed:', error);
                      }
                    }, 100);
                  }
                }
              }}
              title={soundEnabled ? 'Disable Sound' : 'Enable Sound'}
            >
              {soundEnabled ? '🔊' : '🔇'}
            </button>
          </div>
        )}
      </div>

      {isAuthenticated ? (
        <>
          <div className="game-main-content">
        <div className={`game-board ${isProcessing ? 'processing' : ''}`}>
          {isProcessing && (
            <div className="processing-overlay">
              <div className="magic-particles">
                {Array.from({ length: 12 }, (_, i) => (
                  <div
                    key={i}
                    className="magic-particle"
                    style={{
                      left: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 2}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Celebration particles overlay */}
          <div className="celebrations-overlay">
            {celebrations.map(celebration => (
            <div
              key={celebration.id}
              className={`celebration celebration-${celebration.type}`}
              style={{
                left: celebration.x,
                top: celebration.y,
              }}
            >
              {celebration.type === 'mega' && '✨🎆✨'}
              {celebration.type === 'combo' && '🌟💫'}
              {celebration.type === 'normal' && '✨'}
              <div className="celebration-text">
                {celebration.matchLength > 4 ? 'MEGA!' : 
                 celebration.comboLevel > 2 ? `COMBO x${celebration.comboLevel}!` : 
                 `+${celebration.matchLength * 10}`}
              </div>
            </div>
          ))}
        </div>
        
          {board.map((row, rowIndex) => (
            <div key={rowIndex} className="board-row">
              {row.map((gem, colIndex) => {
                const gemKey = `${rowIndex}-${colIndex}`;
                const isSelected = selectedGem?.row === rowIndex && selectedGem?.col === colIndex;
                const isMatched = matchedGems.has(gemKey);
                const isFalling = fallingGems.has(gemKey);
                
                return (
                  <div
                    key={gemKey}
                    className={`gem-cell ${isSelected ? 'selected' : ''} ${isMatched ? 'matched' : ''} ${isFalling ? 'falling' : ''}`}
                    onClick={() => handleGemClick(rowIndex, colIndex)}
                  >
                    <span className="gem">{gem}</span>
                  </div>
                );
              })}
            </div>
          ))}
      </div>

      <div className="game-stats">
        <div className="user-info">
          <div className="player-name">👤 {userName}</div>
        </div>
        
        <div className="stats-row">
          <div className="info-item">
            <span className="info-label">Score:</span>
            <span className="info-value">{score.toLocaleString()}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Target:</span>
            <span className="info-value">{target.toLocaleString()}</span>
          </div>
        </div>
        <div className="stats-row">
          <div className="info-item">
            <span className="info-label">Moves:</span>
            <span className="info-value" style={{color: moves <= 5 ? '#ff6b6b' : 'white'}}>{moves}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Level:</span>
            <span className="info-value">{level}</span>
          </div>
        </div>
        <div className="stats-row">
          <div className="info-item lifeline-display">
            <span className="info-label">💎 Lifelines:</span>
            <span className="info-value lifeline-count" style={{color: '#4ecdc4'}}>{dailyData.lifelines}</span>
          </div>
        </div>
        
        <div className="gem-quest-progress-bar">
          <div className="gem-quest-progress-label">Progress to Level {level + 1}</div>
          <div className="gem-quest-progress-track">
            <div 
              className="gem-quest-progress-fill" 
              style={{ width: `${Math.min((score / target) * 100, 100)}%` }}
            />
            <div className="gem-quest-progress-text">
              {score.toLocaleString()} / {target.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="status-indicators">
          {combo > 0 && (
            <div className="combo-indicator">
              Combo x{combo}!
            </div>
          )}
          {showHint && (
            <div className="hint-indicator">
              {noMovesWithLifelines ? 
                `No moves available! Use a lifeline? (${dailyData.lifelines} remaining)` :
                "Reshuffling board... +5 bonus moves!"
              }
            </div>
          )}
        </div>
      </div>
      </div> {/* End of game-main-content */}

      {(gameOver || levelComplete) && (
        <div className="game-over-modal">
          <div className="modal-content">
            {levelComplete ? (
              <>
                <h2>🎉 Level {level} Complete!</h2>
                <div className="level-complete-stats">
                  <div className="stat-item">
                    <span className="stat-label">Score Achieved:</span>
                    <span className="stat-value">{score.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Target Score:</span>
                    <span className="stat-value">{target.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Next Level Target:</span>
                    <span className="stat-value highlighted">{Math.floor(target * 1.5).toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Moves Remaining:</span>
                    <span className="stat-value bonus">{moves}</span>
                  </div>
                </div>
                <div className="modal-buttons">
                  <button className="next-level-btn" onClick={proceedToNextLevel}>
                    Continue to Level {level + 1} ➡️
                  </button>
                  <button className="play-again-btn secondary" onClick={resetGame}>
                    Restart Game
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>💀 Game Over</h2>
                <div className="game-over-stats">
                  <div className="stat-item">
                    <span className="stat-label">Final Score:</span>
                    <span className="stat-value">{score.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Level Reached:</span>
                    <span className="stat-value">{level}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Target Score:</span>
                    <span className="stat-value">{target.toLocaleString()}</span>
                  </div>
                </div>
                <div className="modal-buttons">
                  <button className="play-again-btn" onClick={resetGame}>
                    Play Again
                  </button>
                  <button className="leaderboard-btn secondary" onClick={async () => {
                    setShowLeaderboard(true);
                    try {
                      const firebaseLeaderboard = await loadLeaderboardFromFirebase();
                      if (firebaseLeaderboard.length > 0) {
                        setLeaderboard(firebaseLeaderboard);
                      }
                    } catch (error) {
                      console.error('Error refreshing leaderboard:', error);
                    }
                  }}>
                    🏆 View Leaderboard
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <div className="game-over-modal">
          <div className="modal-content leaderboard-modal">
            <h2>🏆 Leaderboard</h2>
            <div className="leaderboard-list">
              <h3 className="leaderboard-title">🏆 Top Players 🏆</h3>
              <div className="leaderboard-header">
                <div>Rank</div>
                <div>Player</div>
                <div>Score</div>
                <div>Level</div>
              </div>
              <div className="leaderboard-items">
                {leaderboard.length > 0 ? (
                  leaderboard.map((entry, index) => (
                    <div 
                      key={entry.userId} 
                      className={`leaderboard-item ${entry.userId === userId ? 'current-player' : ''}`}
                    >
                      <div className="rank-cell">{index + 1}</div>
                      <div className="name-cell">{entry.playerName || 'Unknown Player'}</div>
                      <div className="score-cell">{(entry.score || 0).toLocaleString()}</div>
                      <div className="level-cell">Level {entry.level || 1}</div>
                    </div>
                  ))
                ) : (
                  <div className="empty-leaderboard">
                    No scores yet! Be the first to play!
                  </div>
                )}
              </div>
            </div>
            <div className="modal-buttons">
              <button className="play-again-btn" onClick={() => setShowLeaderboard(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Name Prompt Modal */}
      {showNamePrompt && (
        <div className="game-over-modal">
          <div className="modal-content name-prompt-modal">
            <h2>👤 Change Player Name</h2>
            <div className="name-prompt-content">
              <p>Current name: <strong>{userName}</strong></p>
              <input 
                type="text" 
                placeholder="Enter your name"
                maxLength={20}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleNameUpdate(e.target.value);
                  }
                }}
                autoFocus
              />
            </div>
            <div className="modal-buttons">
              <button 
                className="next-level-btn" 
                onClick={(e) => {
                  const input = e.target.parentElement.parentElement.querySelector('input');
                  handleNameUpdate(input.value);
                }}
              >
                Save Name
              </button>
              <button className="play-again-btn secondary" onClick={() => setShowNamePrompt(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daily Limit Modal */}
      {showDailyLimitModal && (
        <div className="game-over-modal">
          <div className="modal-content daily-limit-modal">
            <h2>🌙 Daily Play Complete</h2>
            <div className="daily-limit-content">
              <div className="daily-celebration">
                <div className="celebration-badge">
                  <span className="badge-icon">🎮</span>
                  <span className="badge-text">Session Complete!</span>
                </div>
              </div>
              <div className="daily-stats">
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Lifelines Remaining:</span>
                    <span className="stat-value lifeline-count" style={{color: '#4ecdc4'}}>{dailyData.lifelines}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Lifelines Earned:</span>
                    <span className="stat-value lifeline-earned" style={{color: '#ffd700'}}>{dailyData.totalLifelinesEarned}</span>
                  </div>
                </div>
              </div>
              <div className="comeback-message">
                <p>🎮 You've completed your daily Gem Quest session!</p>
                <div className="countdown-display">
                  <h3>⏰ Next Play Available In:</h3>
                  <div className="countdown-timer">
                    {countdownTime || getCountdownDisplay()}
                  </div>
                </div>
                <p className="encouragement">� Great job today! Come back tomorrow for more gem-matching fun!</p>
              </div>
            </div>
            <div className="modal-buttons">
              <button className="play-again-btn primary" onClick={() => setShowDailyLimitModal(false)}>
                <span className="btn-icon">👋</span>
                <span className="btn-text">See You Tomorrow!</span>
              </button>
              <button className="leaderboard-btn professional" onClick={async () => {
                setShowDailyLimitModal(false);
                setShowLeaderboard(true);
                try {
                  const firebaseLeaderboard = await loadLeaderboardFromFirebase();
                  if (firebaseLeaderboard.length > 0) {
                    setLeaderboard(firebaseLeaderboard);
                  }
                } catch (error) {
                  console.error('Error refreshing leaderboard:', error);
                }
              }}>
                <span className="btn-icon">🏆</span>
                <span className="btn-text">View Leaderboard</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lifeline Modal */}
      {showLifelineModal && (
        <div className="game-over-modal">
          <div className="modal-content lifeline-modal">
            <h2>💎 No Moves Available!</h2>
            <div className="lifeline-content">
              <div className="lifeline-offer">
                <p>🚫 <strong>No more possible moves on the board!</strong></p>
                <p>💎 You have <strong>{dailyData.lifelines} lifeline{dailyData.lifelines !== 1 ? 's' : ''}</strong> available.</p>
                <p>✨ Use a lifeline to shuffle the board and get <strong>+10 bonus moves</strong>?</p>
              </div>
              <div className="lifeline-stats">
                <div className="stat-item">
                  <span className="stat-label">Current Score:</span>
                  <span className="stat-value">{score.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Current Level:</span>
                  <span className="stat-value">{level}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Available Lifelines:</span>
                  <span className="stat-value" style={{color: '#4ecdc4'}}>{dailyData.lifelines}</span>
                </div>
              </div>
            </div>
            <div className="modal-buttons">
              <button className="next-level-btn" onClick={handleUseLifeline}>
                💎 Use Lifeline (+10 moves)
              </button>
              <button className="play-again-btn secondary" onClick={handleDeclineLifeline}>
                🌙 End Session for Today
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lifeline Earned Modal */}
      {showLifelineEarnedModal && (
        <div className="game-over-modal">
          <div className="modal-content lifeline-earned-modal">
            <h2>🎉 Lifeline{earnedLifelineScore?.lifelines > 1 ? 's' : ''} Earned!</h2>
            <div className="lifeline-earned-content">
              <div className="celebration-icon">💎✨</div>
              <p><strong>Congratulations!</strong></p>
              <p>You've reached <strong>{earnedLifelineScore?.score?.toLocaleString()} points</strong> and earned <strong>{earnedLifelineScore?.lifelines} lifeline{earnedLifelineScore?.lifelines > 1 ? 's' : ''}!</strong></p>
              <div className="lifeline-earned-stats">
                <div className="stat-item">
                  <span className="stat-label">New Lifeline Balance:</span>
                  <span className="stat-value highlighted">{dailyData.lifelines}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Earned Today:</span>
                  <span className="stat-value" style={{color: '#ffd700'}}>{dailyData.totalLifelinesEarned}</span>
                </div>
              </div>
              <p className="lifeline-tip">💡 Lifelines let you continue playing when no moves are available!</p>
            </div>
            <div className="modal-buttons">
              <button className="next-level-btn" onClick={() => setShowLifelineEarnedModal(false)}>
                🎮 Continue Playing!
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="game-instructions">
        <h3>How to Play:</h3>
        <ul>
          <li>Click on a gem, then click on an adjacent gem to swap them</li>
          <li>Match 3 or more gems of the same type to score points</li>
          <li>Reach the target score before running out of moves</li>
          <li>Longer matches and combos give bonus points</li>
          <li>Complete levels to advance and face greater challenges</li>
          <li>💎 Earn lifelines by reaching score milestones (5K, 15K, 30K, 50K, 75K, 100K)</li>
          <li>🚫 When no moves are available, use lifelines to continue or wait 12 hours</li>
        </ul>
      </div>
        </>
      ) : (
        <div className="authentication-placeholder">
          <div className="auth-message">
            <h2>🎮 Welcome to Gem Quest!</h2>
            <p>Please wait while we set up your gaming experience...</p>
          </div>
        </div>
      )}

      {/* Welcome Back Modal */}
      {showWelcomeModal && userProfile && (
        <div className="game-over-modal">
          <div className="modal-content welcome-modal">
            <h2>👋 Welcome Back!</h2>
            <div className="welcome-content">
              <div className="user-info-display">
                <h3>Continue as <strong>{userProfile.name}</strong>?</h3>
                <div className="saved-progress">
                  <div className="stat-item">
                    <span className="stat-label">Saved Score:</span>
                    <span className="stat-value highlighted">{userProfile.score.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Saved Level:</span>
                    <span className="stat-value highlighted">{userProfile.level}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Last Played:</span>
                    <span className="stat-value">{new Date(userProfile.lastPlayed).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-buttons">
              <button className="next-level-btn" onClick={handleContinueAsUser}>
                ✅ Continue as {userProfile.name}
              </button>
              <button className="play-again-btn secondary" onClick={handleChangeUser}>
                👤 Play as Different User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New User Modal */}
      {showNewUserModal && (
        <div className="game-over-modal">
          <div className="modal-content new-user-modal">
            <h2>🎮 Welcome to Gem Quest!</h2>
            <div className="new-user-content">
              <p>Enter your name to start your gem-matching adventure!</p>
              <input 
                type="text" 
                placeholder="Enter your name"
                maxLength={20}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateNewUser(e.target.value);
                  }
                }}
                autoFocus
              />
              <div className="game-preview">
                <h4>🎯 Game Features:</h4>
                <ul>
                  <li>💎 Match 3+ gems to score points</li>
                  <li>🎖️ Progress through challenging levels</li>
                  <li>🎁 Earn lifelines for high scores</li>
                  <li>🏆 Compete on the leaderboard</li>
                  <li>💾 Your progress is automatically saved</li>
                </ul>
              </div>
            </div>
            <div className="modal-buttons">
              <button 
                className="next-level-btn" 
                onClick={(e) => {
                  const input = e.target.parentElement.parentElement.querySelector('input');
                  handleCreateNewUser(input.value);
                }}
              >
                🚀 Start Playing!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GemQuest;